import { Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ExamStatusChip from 'Components/ExamStatusChip';
import PersentageText from 'Components/PersentageText';
import { useModal } from 'shared/hooks';
import { SendToStudentsModal } from 'shared/ui';

import { DropdownOfTableButtons } from './ui';
import { iclApi, subjectsApi } from '../../../api';
import copySvg from '../../../assets/img/copy.svg';
import { getStudents } from '../../../Common/Database';
import { tabs } from '../../../constants/checlistTableTabs';
import { examTablePopupsNames } from '../../../constants/examTablePopupsNames';
import { tableSize } from '../../../constants/tableSize';
import { Exam, Option } from '../../../shared/types';
import { ExamStatus, PersonOption } from '../../../shared/types/works';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { getTeachersStudent } from '../../../utils/getTeachersStudent';
import { getTeachersSubjects } from '../../../utils/getTeachersSubjects';
import { LoadingError } from '../../LoadingError';
import ExamFilters from '../ExamFilters';
import TableTabs from '../TableTabs';
import moment from 'moment';
import { formatIclTime, sortIclsByTime } from '../exam-helpers';

interface Props {
  mail: string;
}

const teacherTabs = [
  { tabName: 'My templates', tabKey: tabs.myTemplates },
  { tabName: 'Students Works', tabKey: tabs.studentsWorks },
  { tabName: 'Templates', tabKey: tabs.template },
  { tabName: 'Archived', tabKey: tabs.archived },
];

export default function ExamTableTeacher({ mail }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get('tab');

  const [icls, setIcls] = useState<Exam[]>([]);
  const [iclsForShow, setIclsForShow] = useState<Exam[]>([]);
  const [currentIcl, setCurrentIcl] = useState<Exam | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState('');
  const [idForManipulation, setIdForManipulation] = useState('');
  const [tab, setTab] = useState<string>(tabs.myTemplates);
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [teachersSubjects, setTeachersSubjects] = useState<string[] | null>(null);
  const [click, setClick] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState<Option | null>(null);
  const [studentFilter, setStudentFilter] = useState<PersonOption | null>(null);
  const [classFilter, setClassFilter] = useState<string | null>(null);

  const { isOpen, close, open } = useModal();

  useEffect(() => {
    setTab(tabQuery || tabs.myTemplates);
  }, [tabQuery]);

  useEffect(() => {
    getTeachersSubjects(mail).then(subjects => {
      setTeachersSubjects(subjects);
    });
  }, []);

  useEffect(() => {
    getTeachersStudent(mail).then(students => {
      setStudents(students);
    });
  }, []);

  useEffect(() => {
    if (iclsForShow.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [iclsForShow]);

  useEffect(() => {
    if (teachersSubjects) {
      const updateSubjects = (subjects: Option[]) => {
        const filteredSubjects = subjects.filter(subject => teachersSubjects.includes(subject.name || subject.ruName));

        setSubjects(filteredSubjects);
      };
      const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

      return () => {
        unsubscribe();
      };
    }
  }, [teachersSubjects]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.template) {
      console.log('template');
      const unsubscribe = iclApi.subscribeToTemplateExams({
        setNewICLs: data => {
          setIcls(data);
          setIsLoading(false);
        },
        subjectFilter: subjectFilter?.id,
        classFilter,
      });

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.myTemplates) {
      console.log('my templates');
      const unsubscribe = iclApi.subscribeToTeacherExams({
        setNewICLs: data => {
          setIcls(data);
          setIsLoading(false);
        },
        teacherEmail: mail,
        subjectFilter: subjectFilter?.id,
        classFilter,
        myICLs: true,
      });

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.studentsWorks) {
      console.log('studentWorks');
      const unsubscribe = iclApi.subscribeToTeacherExams({
        setNewICLs: data => {
          setIcls(data);
          setIsLoading(false);
        },
        teacherEmail: mail,
        subjectFilter: subjectFilter?.id,
        classFilter,
        studentFilter,
        studentsWorks: true,
      });

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.archived) {
      const unsubscribe = iclApi.subscribeToTeacherExams({
        setNewICLs: data => {
          setIcls(data);
          setIsLoading(false);
        },
        teacherEmail: mail,
        isActive: false,
        studentFilter,
        subjectFilter: subjectFilter?.id,
        classFilter,
        deleted: true,
      });

      return () => {
        unsubscribe();
      };
    }
  }, [tab, click]);

  useEffect(() => {
    const start = (page - 1) * tableSize;
    const end = start + tableSize;

    setIclsForShow(sortIclsByTime(icls).slice(start, end));
  }, [icls, page, tab]);

  const handleNavigateToExamUpdate = (id: string) => {
    navigate('/exam-update', { state: { ICLId: id } });
  };

  const handleNavigateToPreview = (id: string) => {
    navigate('/exam-preview', { state: { examId: id } });
  };

  const handleDeleteICL = async (id: string) => {
    setIsRemovingLoading(true);

    try {
      await iclApi.removeICL(id);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(t('Successfully removed!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handleOpenDeletePopup = (id: string) => {
    setIsModalOpen(examTablePopupsNames.delete);
    setIdForManipulation(id);
  };

  const handleArchivePopup = (icl: Exam, isActive: boolean) => {
    setCurrentIcl(icl);
    setIsModalOpen(isActive ? examTablePopupsNames.archive : examTablePopupsNames.activate);
  };

  const handleSendForConsideration = async (id: string) => {
    const targetIcl = icls.find(it => it.id === id);

    setIsLoading(true);

    if (targetIcl) {
      if (Object.prototype.hasOwnProperty.call(targetIcl, 'isConsideration')) {
        targetIcl.isConsideration = true;
      } else {
        targetIcl['isConsideration'] = true;
      }

      await iclApi.updateICL(targetIcl).then(() => {
        setIsLoading(false);
        toast.success(t('Successfully sent!'));
      });
    }
  };

  const changeICLStatus = async () => {
    setIsRemovingLoading(true);

    try {
      currentIcl && (await iclApi.updateICL({ ...currentIcl, isActive: !currentIcl.isActive }));
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(currentIcl?.isActive ? t('Successfully archived!') : t('Successfully activated!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handlePopupActions = async () => {
    switch (isModalOpen) {
      case 'delete':
        handleDeleteICL(idForManipulation);
        break;
      case 'copyAsTeacher':
        copyToTeacher(idForManipulation);
        break;
      case 'sendToAdmin':
        handleSendForConsideration(idForManipulation);
        break;
      case examTablePopupsNames.activate:
      case examTablePopupsNames.archive:
        await changeICLStatus();
        break;
    }

    setIsModalOpen('');
  };

  const actionPopupTitle = useMemo(() => {
    switch (isModalOpen) {
      case examTablePopupsNames.delete:
        return 'want to delete exam';
      case examTablePopupsNames.copyAsTeacher:
        return 'copyAsTether';
      case examTablePopupsNames.sendToAdmin:
        return 'sendToAdmin';
      case examTablePopupsNames.archive:
        return 'archive';
      case examTablePopupsNames.activate:
        return 'activate';
      default:
        return '';
    }
  }, [isModalOpen]);

  const actionPopupButtonTitle = useMemo(() => {
    switch (isModalOpen) {
      case examTablePopupsNames.delete:
        return 'delete';
      case examTablePopupsNames.copyAsTeacher:
        return 'copyAsTetherButton';
      case examTablePopupsNames.sendToAdmin:
        return 'sendToAdminButton';
      case examTablePopupsNames.archive:
        return 'archiveButton';
      case examTablePopupsNames.activate:
        return 'activateButton';
      default:
        return '';
    }
  }, [isModalOpen]);

  const copyToTeacher = async (id: string) => {
    setIsLoading(true);
    const targetIcl = icls.find(it => it.id === id);

    if (targetIcl) {
      await iclApi.copyICLToTeacher(targetIcl, mail).then(() => {
        setIsLoading(false);
        toast.success(t('Successfully copied!'));
      });
    }
  };

  const handleOpenCopyPopup = (id: string) => {
    setIsModalOpen(examTablePopupsNames.copyAsTeacher);
    setIdForManipulation(id);
  };

  const renderPreviewButton = (id: string) => {
    if (tab === tabs.template) {
      return (
        <button className="btn btn-primary more" onClick={() => handleNavigateToPreview(id)}>
          {t('preview')}
        </button>
      );
    }
  };

  const renderGoToExamButton = (id: string) => {
    return (
      <button className="btn more" onClick={() => navigate('/exam-teacher-new', { state: { examId: id } })}>
        {t('transition-in')}
      </button>
    );
  };

  const renderEditButton = (id: string, isTemplate: boolean) => {
    if (isTemplate) {
      return (
        <Tooltip
          title={t('copyAsTether')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
            onClick={() => handleOpenCopyPopup(id)}
          >
            <img src={copySvg} alt="copy" />
          </button>
        </Tooltip>
      );
    } else {
      return (
        tab !== tabs.archived &&
        tab !== tabs.studentsWorks && (
          <button className="btn btn-primary more" onClick={() => handleNavigateToExamUpdate(id)}>
            {t('editing')}
          </button>
        )
      );
    }
  };

  const handleOpenSendPopup = (id: string) => {
    setIsModalOpen(examTablePopupsNames.sendToAdmin);
    setIdForManipulation(id);
  };

  const openSendToStudentsModal = (icl: Exam) => {
    setCurrentIcl(icl);
    open();
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  const getFIOFromEmail = (email: string) => {
    for (const student of students) {
      if (student.email?.replace(/\./g, '') === email) {
        return student.FIO;
      }
    }

    return email;
  };

  const renderRows = () => {
    return iclsForShow.map(icl => {
      const { id, theme, subject, class: studentClass, isTemplate, studentEmail, status, totalPoint } = icl;

      const formattedTime = formatIclTime(icl.time);

      return (
        <TableRow key={id}>
          <TableCell
            sx={{
              width: 200,
              maxWidth: 200,
              overflow: 'hidden',
              position: 'sticky',
              left: 0,
              backgroundColor: 'white',
              boxShadow: 'inset 0 -1px 0 rgba(224, 224, 224, 1)',
            }}
          >
            {theme}
          </TableCell>
          <TableCell sx={{ minWidth: 100 }}>{t(studentClass)}</TableCell>
          <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
          {(tab === tabs.studentsWorks || tab === tabs.archived) && (
            <>
              <TableCell>{studentEmail ? getFIOFromEmail(studentEmail) : ''}</TableCell>

              <TableCell>
                <ExamStatusChip value={status} />
              </TableCell>

              <TableCell>
                <PersentageText value={totalPoint ? Number(totalPoint) : 0} />
              </TableCell>

              {tab === tabs.studentsWorks && (
                <TableCell sx={{ width: 100, minWidth: 100, textAlign: 'center' }}>
                  <div className="flex flex-col items-center whitespace-nowrap leading-[18px]">
                    <span>{formattedTime.date}</span>
                    <span>{formattedTime.time}</span>
                  </div>
                </TableCell>
              )}
            </>
          )}

          <TableCell sx={{ width: 180, minWidth: 180 }}>
            <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
              {renderEditButton(id, isTemplate)}

              {!isTemplate && tab !== tabs.archived && (
                <Tooltip
                  title={t('deleteExam')}
                  componentsProps={{
                    popper: {
                      sx: {
                        ['& .MuiTooltip-arrow']: {
                          color: '#9CDFFC',
                        },
                        ['& .MuiTooltip-tooltip']: {
                          backgroundColor: '#9CDFFC',
                          color: '#000000',
                          fontSize: '14px',
                          padding: '14px',
                          borderRadius: '8px',
                        },
                      },
                    },
                  }}
                  arrow
                >
                  <button
                    className="btn more small h-[40px] w-[40px] rounded-[8px]"
                    onClick={() => handleOpenDeletePopup(id)}
                  >
                    <span className="icon icon-delete text-red-600" />
                  </button>
                </Tooltip>
              )}

              {(tab === tabs.archived || tab === tabs.studentsWorks) && renderGoToExamButton(id)}

              {renderPreviewButton(id)}

              {tab !== tabs.template && tab !== tabs.studentsWorks && tab !== tabs.archived && (
                <DropdownOfTableButtons
                  icl={icl}
                  tab={tab}
                  isLoading={isLoading}
                  handleArchivePopup={handleArchivePopup}
                  handleOpenSendPopup={handleOpenSendPopup}
                  openSendToStudentsModal={openSendToStudentsModal}
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  const handleApplyFilters = () => {
    setClick(!click);
  };

  const handleResetFilters = () => {
    setClick(!click);
    setStudentFilter(null);
    setSubjectFilter(null);
    setClassFilter(null);
  };

  const handleChangeTab = (tab: string) => {
    setTab(tab);
    setPage(1);
    handleResetFilters();
  };

  const getEmptyMessage = () => {
    switch (tab) {
      case tabs.myTemplates:
        return t('noMyExamTemplates');

      case tabs.studentsWorks:
        return t('noStudentExams');

      case tabs.template:
        return t('noExamTemplates');

      case tabs.archived:
        return t('noArchivedExams');

      default:
        return '';
    }
  };

  return (
    <LoadingError isLoading={isLoading && !icls}>
      <div className="lg:px-[15px]">
        <div className="mb-[20px] flex gap-[8px] lg:block">
          <TableTabs setTab={handleChangeTab} tab={tab} configArr={teacherTabs} />

          {students && subjects && (
            <ExamFilters
              subject={subjectFilter}
              student={studentFilter}
              studentClass={classFilter}
              setClassFilter={setClassFilter}
              setStudentFilter={setStudentFilter}
              setSubjectFilter={setSubjectFilter}
              students={students}
              handleApply={handleApplyFilters}
              handleReset={handleResetFilters}
              click={click}
              subjects={subjects}
              tab={tab}
            />
          )}
        </div>

        <div className="relative mb-[20px] rounded-[24px] bg-white [box-shadow:10px_10px_0px_0px_#EBEAE7] lg:px-[20px] lg:py-[15px]">
          <TableContainer sx={{ borderRadius: '24px' }}>
            {iclsForShow.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {getEmptyMessage()}
                </TableCell>
              </TableRow>
            ) : (
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: 200,
                        maxWidth: 200,
                        overflow: 'hidden',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                      }}
                    >
                      {t('theme')}
                    </TableCell>

                    <TableCell sx={{ minWidth: 100 }}>{t('class')}</TableCell>

                    <TableCell>{t('subject')}</TableCell>

                    {(tab === tabs.studentsWorks || tab === tabs.archived) && (
                      <>
                        <TableCell>{t('alreadySend')}</TableCell>

                        <TableCell>{t('exam status')}</TableCell>

                        <TableCell>{t('exam grade')}</TableCell>

                        {tab === tabs.studentsWorks && (
                          <TableCell sx={{ width: 100, minWidth: 100, textAlign: 'center' }}>{t('time')}</TableCell>
                        )}
                      </>
                    )}

                    <TableCell sx={{ width: 180, minWidth: 180 }} />
                  </TableRow>
                </TableHead>

                <TableBody>{renderRows()}</TableBody>
              </Table>
            )}
          </TableContainer>
        </div>

        {iclsForShow.length > 0 && (
          <Stack alignItems="center">
            <Pagination
              sx={{
                '& .MuiPaginationItem-page.Mui-selected': {
                  backgroundColor: 'white',
                  border: '1px solid #52C7FA',
                },
                '& .MuiPaginationItem-page': {
                  color: '#656D7B',
                },
              }}
              shape={'rounded'}
              page={page}
              onChange={(_, page) => handleChangePage(page)}
              count={Math.ceil(icls.length / tableSize)}
            />
          </Stack>
        )}

        <ItemActionModal
          isOpen={Boolean(isModalOpen)}
          close={() => setIsModalOpen('')}
          title={actionPopupTitle}
          onHandle={() => handlePopupActions()}
          isSubmitButtonDisabled={isRemovingLoading}
          buttonTitle={actionPopupButtonTitle}
        />
        <SendToStudentsModal currentICL={currentIcl} isOpen={isOpen} onClose={close} />
      </div>
    </LoadingError>
  );
}
